import React from 'react'
import {
  BarChart3,
  Building2,
  Car,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  HeartPulse,
  ListChecks,
  MessageCircle,
  Search,
  ShieldCheck,
  Target,
  TrendingDown,
  Users,
  Wrench,
} from 'lucide-react'

/** 한국어 카드 문단: 브라우저 기본 break-all 끊김 완화 (단어·어절 단위 줄바꿈 우선) */
const KO_LINE = 'break-keep break-words'

function getGridColsClass(columns = 3) {
  if (columns === 2) return 'grid grid-cols-1 md:grid-cols-2 gap-4'
  if (columns === 4) return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'
  return 'grid grid-cols-1 md:grid-cols-3 gap-4'
}

function renderMedia(resolveAssetUrl, fileId) {
  if (!fileId) return null
  return resolveAssetUrl(fileId)
}

function getLandingIconComponent(icon, title = '') {
  const key = `${icon || ''} ${title || ''}`
  if (/자동차|운전|차량|🚗/.test(key)) return Car
  if (/화재|건물|주택|상가|🏠|🔥/.test(key)) return Building2
  if (/간병|돌봄|건강|🛏️/.test(key)) return HeartPulse
  if (/가족|고객|👨‍👩‍👧|🤝/.test(key)) return Users
  if (/조회|확인|분석|점검|🔎|🔍/.test(key)) return Search
  if (/서류|증권|정보|입력|🧾|📄|📋|📝/.test(key)) return FileText
  if (/비교|구조|통계|📊|⚖️/.test(key)) return BarChart3
  if (/중복|보험료|절감|부담|📉|✂️/.test(key)) return TrendingDown
  if (/재구성|리모델링|정비|🛠️/.test(key)) return Wrench
  if (/상담|문의|연락|전화|💬|📞|☎️|📲/.test(key)) return MessageCircle
  if (/목적|목표|노후|🎯/.test(key)) return Target
  if (/체크|완료|✅|✓/.test(key)) return CheckCircle2
  if (/보장|보호|배상|🛡️/.test(key)) return ShieldCheck
  if (/절차|단계|진행/.test(key)) return ListChecks
  return ClipboardCheck
}

export function CategoryLandingIconBadge({ icon, title, className = '' }) {
  const Icon = getLandingIconComponent(icon, title)
  return (
    <span className={`inline-flex items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ${className}`}>
      <Icon className="h-6 w-6" aria-hidden="true" />
    </span>
  )
}

const UNSAFE_DEFAULT_STAT_LABELS = [
  '평균 보장 개선율',
  '불필요 보험료 절감',
  '분석 만족도',
  '누적 분석 건수',
  '계약 유지율',
  '상담 건수',
  '상담 답변율',
  '고객 만족도',
  '평균 신청 시간',
  '입력 시간',
  '응답 속도',
]

const TEMPLATE_STAT_VALUES = ['1분', '1분 이내', '1분 신청', '빠름', '4.7/5.0', '4.8/5.0', '23%', '월 11만원', '5,000건+', '92.4%', '1248', '98.1%']

export function normalizeLandingStatItems(items = []) {
  if (!Array.isArray(items)) return []
  return items
    .map((item) => ({
      ...item,
      label: String(item?.label || '').trim(),
      value: String(item?.value || '').trim(),
    }))
    .filter((item) => item.label && item.value)
    .filter((item) => !UNSAFE_DEFAULT_STAT_LABELS.includes(item.label))
    .filter((item) => !(TEMPLATE_STAT_VALUES.includes(item.value) && /상담|신청|입력|응답|만족도|개선율|절감|건수/.test(item.label)))
}

function FaqSection({ section }) {
  const items = Array.isArray(section?.items) ? section.items : []
  return (
    <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
      {section?.title && <div className={`mb-4 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
      <div className="space-y-3">
        {items.map((item, idx) => (
          <details key={idx} className="group rounded-2xl border bg-slate-50 px-5 py-4">
            <summary className={`cursor-pointer list-none pr-6 text-base font-semibold leading-7 text-slate-800 ${KO_LINE}`}>
              {item?.question || `질문 ${idx + 1}`}
            </summary>
            <div className={`mt-3 text-base leading-7 text-slate-600 whitespace-pre-line ${KO_LINE}`}>
              {item?.answer || ''}
            </div>
          </details>
        ))}
      </div>
    </div>
  )
}

function TestimonialSection({ section }) {
  const items = Array.isArray(section?.items) ? section.items : []
  return (
    <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
      {section?.title && <div className={`mb-5 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
      <div className={getGridColsClass(items.length >= 3 ? 3 : 2)}>
        {items.map((item, idx) => (
          <div key={idx} className="rounded-2xl border bg-slate-50 p-5">
            <div className={`text-base leading-7 text-slate-700 whitespace-pre-line ${KO_LINE}`}>
              "{item?.content || ''}"
            </div>
            <div className="mt-5 text-base font-semibold text-slate-900">
              {item?.name || `고객 ${idx + 1}`}
            </div>
            {item?.subtitle && <div className="mt-1 text-sm text-slate-500">{item.subtitle}</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

function SectionCard({ section, resolveAssetUrl, variant = 'default' }) {
  const type = String(section?.type || '').toLowerCase()

  if (type === 'image' || type === 'infographic') {
    const src = renderMedia(resolveAssetUrl, section?.fileId || section?.imageFileId)
    return (
      <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
        {section?.title && <div className={`mb-4 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
        {src ? (
          <img src={src} alt={section?.caption || section?.title || ''} className="w-full rounded-2xl" />
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-12 text-center text-base text-slate-400">
            인포그래픽/이미지 영역
          </div>
        )}
        {section?.caption && <div className={`mt-4 text-center text-base leading-7 text-slate-600 ${KO_LINE}`}>{section.caption}</div>}
      </div>
    )
  }

  if (type === 'icon-grid' || type === 'benefit-cards') {
    const items = Array.isArray(section?.items) ? section.items : []
    const columns = section?.columns || 3
    return (
      <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
        {section?.title && <div className={`mb-5 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
        <div className={getGridColsClass(columns)}>
          {items.map((item, idx) => (
            <div key={idx} className={`rounded-2xl border p-5 text-center ${type === 'benefit-cards' ? 'shadow-sm' : ''}`}>
              <div className="mb-3 flex justify-center">
                <CategoryLandingIconBadge icon={item?.icon} title={item?.title} className="h-12 w-12" />
              </div>
              <div className={`mb-2 text-lg font-semibold leading-7 text-slate-900 ${KO_LINE}`}>{item?.title || ''}</div>
              <div className={`text-base leading-7 text-slate-600 whitespace-pre-line ${KO_LINE}`}>{item?.description || ''}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (type === 'process-steps') {
    const steps = Array.isArray(section?.steps) ? section.steps : []
    const isVertical = section?.layout === 'vertical'
    return (
      <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
        {section?.title && <div className={`mb-5 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
        <div className={isVertical ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-3 gap-4'}>
          {steps.map((step, idx) => (
            <div key={idx} className={`flex ${isVertical ? 'flex-row text-left' : 'flex-col text-center'} items-center gap-4 rounded-2xl border p-5`}>
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-lg font-bold text-blue-700">
                {step?.number || idx + 1}
              </div>
              <div className="flex-1">
                <div className={`mb-2 ${isVertical ? '' : 'flex justify-center'}`}>
                  <CategoryLandingIconBadge icon={step?.icon} title={step?.title} className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700" />
                </div>
                <div className={`mb-2 text-lg font-semibold leading-7 text-slate-900 ${KO_LINE}`}>{step?.title || ''}</div>
                <div className={`text-base leading-7 text-slate-600 whitespace-pre-line ${KO_LINE}`}>{step?.description || ''}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (type === 'stats') {
    const items = normalizeLandingStatItems(section?.items)
    if (items.length === 0) return null
    return (
      <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
        {section?.title && <div className={`mb-4 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((item, idx) => (
            <div key={idx} className="rounded-2xl border p-4 text-center">
              <div className="text-2xl font-extrabold text-slate-900">{item?.value || '-'}</div>
              <div className={`mt-2 text-sm text-slate-500 ${KO_LINE}`}>{item?.label || ''}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (type === 'faq') {
    return <FaqSection section={section} />
  }

  if (type === 'testimonial') {
    return <TestimonialSection section={section} />
  }

  if (type === 'video') {
    const src = renderMedia(resolveAssetUrl, section?.url || section?.fileId)
    return (
      <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
        {section?.title && <div className={`mb-3 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
        {src ? (
          <video controls className="w-full rounded-2xl" src={src} />
        ) : (
          <div className="text-base text-slate-500">영상이 아직 연결되지 않았습니다.</div>
        )}
        {section?.caption && <div className={`mt-3 text-base leading-7 text-slate-600 ${KO_LINE}`}>{section.caption}</div>}
      </div>
    )
  }

  if (type === 'comparison') {
    const left = section?.before || {}
    const right = section?.after || {}
    return (
      <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
        {section?.title && <div className={`mb-5 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="mb-2 text-sm font-semibold text-slate-500">{left?.label || 'Before'}</div>
            <div className={`mb-2 text-lg font-semibold text-slate-900 ${KO_LINE}`}>{left?.title || ''}</div>
            <div className={`text-base leading-7 text-slate-600 whitespace-pre-line ${KO_LINE}`}>{left?.description || ''}</div>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
            <div className="mb-2 text-sm font-semibold text-blue-600">{right?.label || 'After'}</div>
            <div className={`mb-2 text-lg font-semibold text-slate-900 ${KO_LINE}`}>{right?.title || ''}</div>
            <div className={`text-base leading-7 text-slate-700 whitespace-pre-line ${KO_LINE}`}>{right?.description || ''}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-3xl border p-6 md:p-7 bg-white shadow-sm">
      {section?.title && <div className={`mb-3 text-xl font-bold text-slate-900 ${KO_LINE}`}>{section.title}</div>}
      <div className={`text-base leading-8 text-slate-700 whitespace-pre-line ${KO_LINE}`}>{section?.content || ''}</div>
    </div>
  )
}

export default function CategoryLandingSectionList({
  sections = [],
  resolveAssetUrl,
  filter,
  variant = 'default',
}) {
  const safeSections = Array.isArray(sections) ? sections : []
  return (
    <>
      {safeSections
        .filter((section) => (typeof filter === 'function' ? filter(section) : true))
        .map((section, idx) => (
          <SectionCard
            key={`${String(section?.type || 'section')}-${idx}`}
            section={section}
            resolveAssetUrl={resolveAssetUrl}
            variant={variant}
          />
        ))}
    </>
  )
}
